import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Heart, Sparkles, Package, ArrowRight } from 'lucide-react';
import SewingPattern from '@/components/SewingPattern';

export default function About() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 px-4 md:px-8 bg-gradient-to-br from-pockett-purple/20 via-pockett-pink/10 to-pockett-blue/20 overflow-hidden">
        <SewingPattern opacity={0.05} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <p className="text-pockett-purple font-medium mb-4">Meet the Maker</p>
              <h1 className="font-heading font-bold text-4xl md:text-5xl text-pockett-gray mb-6">
                Hi, I'm{' '}
                <span className="text-pockett-purple">Shari</span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                The maker behind Pockett Designs.
              </p>
            </div>
            <div className="animate-fade-in" style={{ animationDelay: '200ms' }}>
              <div className="aspect-square rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-pink-100 via-purple-50 to-blue-100 flex items-center justify-center p-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_fabric-boutique-7/artifacts/p2ihp03e_Text.png"
                  alt="Pockett Designs Logo"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-muted-foreground leading-relaxed mb-6 animate-fade-in">
              Pockett Designs began with my love of sewing practical pieces that still feel personal and full of character. 
              What started as a creative passion has grown into a small, family-run Australian business.
            </p>
            
            <p className="text-lg text-muted-foreground leading-relaxed mb-6 animate-fade-in" style={{ animationDelay: '100ms' }}>
              Every item is sewn by hand in small batches or created as a one-of-a-kind piece. From baby essentials 
              and quilted pouches to fabric accessories and custom orders, I focus on making useful products that 
              are durable, thoughtfully finished and enjoyable to use.
            </p>
            
            <p className="text-lg text-muted-foreground leading-relaxed mb-6 animate-fade-in" style={{ animationDelay: '200ms' }}>
              Sewing is my creative happy place, and I love helping customers turn a favourite fabric, thoughtful idea 
              or special occasion into something meaningful.
            </p>
            
            <p className="text-lg text-muted-foreground leading-relaxed mb-6 animate-fade-in" style={{ animationDelay: '300ms' }}>
              Whether you're shopping for an everyday essential, a personal gift or a custom creation, I hope you 
              find something that feels just right.
            </p>
            
            <p className="text-lg text-pockett-purple font-medium leading-relaxed animate-fade-in" style={{ animationDelay: '400ms' }}>
              Thank you for supporting Pockett Designs and the person behind every stitch.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-gradient-to-b from-white to-pockett-purple/5 relative">
        <SewingPattern opacity={0.03} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-pockett-gray text-center mb-12">
            What Makes Pockett Designs Special
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow animate-fade-in">
              <div className="w-16 h-16 rounded-full bg-pockett-purple/10 flex items-center justify-center mx-auto mb-6">
                <Heart className="h-8 w-8 text-pockett-purple" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-pockett-gray mb-3">
                Made with Love
              </h3>
              <p className="text-muted-foreground">
                Every stitch, every fabric choice, and every finished piece reflects 
                the care that goes into this business.
              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow animate-fade-in" style={{ animationDelay: '100ms' }}>
              <div className="w-16 h-16 rounded-full bg-pockett-pink/10 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="h-8 w-8 text-pockett-pink" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-pockett-gray mb-3">
                Small Batch & One-of-a-Kind
              </h3>
              <p className="text-muted-foreground">
                Each piece is handcrafted in small batches or as unique creations. 
                No two items are exactly alike!
              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow animate-fade-in" style={{ animationDelay: '200ms' }}>
              <div className="w-16 h-16 rounded-full bg-pockett-blue/10 flex items-center justify-center mx-auto mb-6">
                <Package className="h-8 w-8 text-pockett-blue" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-pockett-gray mb-3">
                Custom Orders Welcome
              </h3>
              <p className="text-muted-foreground">
                Looking for something bespoke? I love creating meaningful, 
                personalised pieces just for you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* More Story */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-muted-foreground leading-relaxed mb-6 animate-fade-in">
              Pockett Designs is very much a labour of love. It's not a typical 9–5 — most days are 
              spent sewing, packing orders, creating content, dreaming up new designs, and connecting 
              with customers — and I wouldn't have it any other way.
            </p>
            
            <p className="text-lg text-muted-foreground leading-relaxed mb-6 animate-fade-in" style={{ animationDelay: '100ms' }}>
              Whether you're looking for a thoughtful gift, something custom-made for you or your 
              little one, or simply a splash of handmade magic for everyday life, I hope you find 
              something here that feels special.
            </p>
            
            <div className="bg-gradient-to-br from-pockett-purple/10 via-pockett-pink/10 to-pockett-blue/10 rounded-2xl p-8 text-center mt-12 animate-fade-in" style={{ animationDelay: '200ms' }}>
              <p className="text-xl text-pockett-gray font-heading font-semibold mb-2">
                Thank you sew much for supporting Pockett Designs
              </p>
              <p className="text-muted-foreground">
                and my small family-run business — it truly means the world 🤍
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-gradient-to-b from-white to-pockett-purple/5 relative">
        <SewingPattern opacity={0.03} />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-pockett-gray text-center mb-12">
            A Glimpse of My Work
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="aspect-square rounded-xl overflow-hidden shadow-lg group">
              <img 
                src="https://customer-assets.emergentagent.com/job_a5c2e806-76ac-45b9-a482-9007f3bbaad7/artifacts/8da5cv3m_Photoroom_20260214_115536.png"
                alt="Pouches"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="aspect-square rounded-xl overflow-hidden shadow-lg group">
              <img 
                src="https://customer-assets.emergentagent.com/job_a5c2e806-76ac-45b9-a482-9007f3bbaad7/artifacts/ucw9690y_Photoroom_20260131_055956.png"
                alt="Baby items"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="aspect-square rounded-xl overflow-hidden shadow-lg group">
              <img 
                src="https://customer-assets.emergentagent.com/job_a5c2e806-76ac-45b9-a482-9007f3bbaad7/artifacts/90pqyol8_PhotoRoom_20230704_212157.png"
                alt="Heat packs"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="aspect-square rounded-xl overflow-hidden shadow-lg group">
              <img 
                src="https://customer-assets.emergentagent.com/job_a5c2e806-76ac-45b9-a482-9007f3bbaad7/artifacts/9tltuzu5_Photoroom_20260126_155447.png"
                alt="Book sleeves"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Instagram CTA */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-pockett-gray mb-4">
            Follow the Journey
          </h2>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            See the latest creations, behind-the-scenes moments, and new designs on Instagram.
          </p>
          <Button 
            asChild
            className="bg-gradient-to-r from-pockett-purple to-pockett-pink hover:opacity-90 rounded-full px-8 h-12 font-semibold"
            data-testid="instagram-btn"
          >
            <a href="https://instagram.com/pockett.designs" target="_blank" rel="noopener noreferrer">
              @pockett.designs
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-gradient-to-br from-pockett-purple/10 via-pockett-pink/10 to-pockett-blue/10 relative">
        <SewingPattern opacity={0.04} />
        
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-pockett-gray mb-4">
            Ready to Shop?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            Browse the collection and find the perfect handmade treasure for you or someone special.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              asChild
              className="bg-pockett-purple hover:bg-pockett-purple/90 rounded-full px-8 h-12 font-semibold hover:scale-105 transition-transform"
              data-testid="about-shop-btn"
            >
              <Link to="/shop">
                Shop Now
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button 
              variant="outline"
              asChild
              className="rounded-full px-8 h-12 font-semibold border-2 border-pockett-purple/30 hover:border-pockett-purple"
              data-testid="about-contact-btn"
            >
              <a href="mailto:hello@pockettdesigns.com">
                Get in Touch
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
